<script lang="ts">
  import { onMount } from 'svelte';
  import AppShell from '../components/AppShell.svelte';
  import Icon from '../components/Icon.svelte';
  import KpiCard from '../components/KpiCard.svelte';
  import ChartArea from '../components/ChartArea.svelte';
  import ChartBars from '../components/ChartBars.svelte';
  import FunnelSnap from '../components/FunnelSnap.svelte';
  import {
    fetchOverviewKpi,
    fetchDaily,
    fetchFunnelSnapshot,
    type OverviewKpi,
    type DailyPoint,
    type FunnelSnapshot,
  } from '../lib/dashboards-api';
  import { fmtInt, fmtUah, fmtPct, fmtRelative } from '../lib/format';

  let kpi = $state<OverviewKpi | null>(null);
  let daily = $state<DailyPoint[]>([]);
  let funnels = $state<FunnelSnapshot[]>([]);
  let loading = $state(true);
  let err = $state<string | null>(null);

  onMount(async () => {
    try {
      const [k, d, f] = await Promise.all([
        fetchOverviewKpi(),
        fetchDaily(90),
        fetchFunnelSnapshot(7),
      ]);
      kpi = k;
      daily = d;
      funnels = f;
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    } finally {
      loading = false;
    }
  });

  const grossSeries = $derived(daily.map((p) => p.grossCents / 100));
  const newPersonsSeries = $derived(daily.map((p) => p.newPersons));
  const totalGross = $derived(grossSeries.reduce((a, b) => a + b, 0));
  const totalNew = $derived(newPersonsSeries.reduce((a, b) => a + b, 0));
  const sparkActive = $derived(daily.slice(-8).map((p) => p.newPersons));
</script>

<AppShell active="overview">
  <div class="mx-auto max-w-[1200px]">
    <header class="mb-5 flex items-end justify-between gap-3 flex-wrap">
      <div>
        <h1 class="text-2xl font-bold leading-tight">Огляд</h1>
        <p class="mt-1 text-[12px] text-[var(--color-text-muted)]">
          {#if kpi?.dataStartAt && kpi?.dataEndAt}
            Дані за період {new Date(kpi.dataStartAt).toLocaleDateString('uk-UA', { day: 'numeric', month: 'short', year: 'numeric' })}
            — {new Date(kpi.dataEndAt).toLocaleDateString('uk-UA', { day: 'numeric', month: 'short', year: 'numeric' })}
          {:else}
            Ключові метрики · оновлюються кожні 60 с
          {/if}
          {#if kpi?.lastEventAt}· останній запис {fmtRelative(kpi.lastEventAt)}{/if}
        </p>
      </div>
      <div class="flex items-center gap-2">
        <div class="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border-3)] bg-white px-2.5 py-1 text-[11.5px] font-medium">
          <Icon name="date" size={13} /> Усі дані <Icon name="chevronDown" size={13} />
        </div>
      </div>
    </header>

    {#if loading}
      <div class="py-20 text-center text-sm text-[var(--color-text-muted)]">Завантаження…</div>
    {:else if err}
      <div class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{err}</div>
    {:else if kpi}
      <!-- KPI row — повні дані по всій історії; delta — ост.30 днів даних vs попередні 30 -->
      <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
        <KpiCard
          eyebrow="Усього персон"
          value={fmtInt(kpi.activePersons)}
          deltaPct={kpi.activePersonsDeltaPct}
          deltaSuffix="нові 7д"
          spark={sparkActive}
        />
        <KpiCard
          eyebrow="Avg / місяць"
          value={fmtUah(kpi.mrrCents)}
          deltaPct={kpi.mrrDeltaPct}
          deltaSuffix="ост.30д"
        />
        <KpiCard
          eyebrow="Загальна виручка"
          value={fmtUah(kpi.gross30dCents)}
          deltaPct={kpi.gross30dDeltaPct}
          deltaSuffix="ост.30д"
        />
        <KpiCard
          eyebrow="Платників"
          value={fmtInt(kpi.newPersons7d)}
          deltaPct={kpi.newPersons7dDeltaWoWPct}
          deltaSuffix="ост.30д"
        />
        <KpiCard
          eyebrow="Refund rate"
          value={fmtPct(kpi.churn30dPct).replace('%', '')}
          valueSub="%"
          deltaPct={kpi.churn30dDeltaPp}
          deltaSuffix="п.п."
          invertDelta
        />
        <KpiCard
          eyebrow="ARPU (на платника)"
          value={fmtUah(kpi.arpuCents)}
          deltaPct={kpi.arpuDeltaPct}
          deltaSuffix="ост.30д"
        />
      </div>

      <!-- Charts row -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-3.5 mb-5">
        <ChartArea
          title="Gross revenue · 90 днів"
          sub="Брутто оплати по днях, UAH"
          points={grossSeries}
          footLeft="Gross"
          footRight={`Σ ${fmtUah(Math.round(totalGross * 100), { compact: true })}`}
        />
        <ChartBars
          title="Нові юзери · 90 днів"
          sub="Перший fy.auth.login на день"
          bars={newPersonsSeries}
          footLeft="Нові юзери"
          footRight={`Σ ${fmtInt(totalNew)} · сьогодні ${fmtInt(newPersonsSeries[newPersonsSeries.length - 1] ?? 0)}`}
        />
      </div>

      <!-- Funnels snapshot -->
      <section>
        <div class="flex items-end justify-between mb-2.5">
          <div>
            <div class="text-[13px] font-bold">Воронки — поточний стан</div>
            <div class="text-[11px] text-[var(--color-text-muted)]">
              Конверсія крізь усі стадії за останні 7 днів
            </div>
          </div>
          <a
            href="#/funnels"
            class="text-[12px] font-semibold text-[var(--color-pu-3)] no-underline hover:underline"
          >Усі воронки →</a>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {#each funnels.slice(0, 3) as f (f.slug)}
            <FunnelSnap funnel={f} />
          {/each}
          {#if funnels.length === 0}
            <div class="col-span-3 rounded-xl border border-dashed border-[var(--color-border-3)] bg-[var(--color-bg-2)] p-6 text-center text-sm text-[var(--color-text-muted)]">
              Немає активних воронок. Створіть через Settings → Воронки.
            </div>
          {/if}
        </div>
      </section>
    {/if}
  </div>
</AppShell>
